import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AppDispatch, RootReducerType } from "@store/store";
import {
  getDocumentsListThunk,
  getUploadedFileByIdThunk,
  saveRowUndertakingThunk,
} from "@modules/stepper-row/redux/thunk/documentThunk";
import UnderTackingListDetail, {
  getDocumentArray,
} from "@modules/stepper-row/components/UnderTackingListDetail";

type LegacyUnderTakingConsentRowProps = {
  collapsed: boolean;
  isDetailsPage?: boolean;
  legacyContent?: boolean;
  setSubmitHandler?: React.Dispatch<
    React.SetStateAction<(() => Promise<void>) | null>
  >;
  setIsConsentValid?: (valid: boolean) => void;
};

export const LegacyUnderTakingConsentRow: React.FC<
  LegacyUnderTakingConsentRowProps
> = ({ isDetailsPage, setSubmitHandler, setIsConsentValid, legacyContent }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();

  const {
    category_id,
    access_id,
    amenity,
    utility_type,
    start_chainage_km,
    start_chainage_m,
    end_chainage_km,
    end_chainage_m,
    district,
    state,
    national_highway_number,
    utilized_nh_land_area,
    proposed_utility_alignment,
    highway_category,
  } = useSelector((state: RootReducerType) => state.accessLegacy);

  const { profile } = useSelector((state: RootReducerType) => state.profile);

  const [consentCheck, setConsentCheck] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [allDocuments, setAllDocuments] = useState<any[]>([]);

  const urlApplicationId = searchParams.get("row");
  const applicationID = urlApplicationId || access_id;

  // const access_id = useMemo(
  //     () => urlApplicationId || access_id,
  //     [urlApplicationId, access_id],
  // );

  const undertakings = useMemo(
    () => getDocumentArray(amenity, utility_type, legacyContent),
    [amenity, utility_type],
  );

  const details = {
    start_chainage_km,
    start_chainage_m,
    end_chainage_km,
    end_chainage_m,
    district,
    state,
    national_highway_number,
    highway_category,
    proposed_utility_alignment,
  };

  const createUndertakingPayload = useCallback(() => {
    return {
      application_id: applicationID,
      undertaking_documents: undertakings.map((undertaking) => ({
        document_id: undertaking.document_id,
        document_name: undertaking.title,
        consent_text: undertaking.description,
      })),
    };
  }, [access_id, undertakings]);

  const fetchAndMergeDocuments = useCallback(async () => {
    if (!access_id || !category_id) {
      return;
    }

    try {
      const [documentListData, uploadedFileById] = await Promise.all([
        dispatch(
          getDocumentsListThunk({
            category_id: Number(category_id),
            access_id: access_id,
            document: "undertaking",
          }),
        ),
        dispatch(
          getUploadedFileByIdThunk({
            access_id: access_id,
            category_id: Number(category_id),
            document: "undertaking",
          }),
        ),
      ]);

      const baseDocs = documentListData?.payload?.data || [];
      const uploadedFiles = uploadedFileById?.payload?.data?.files || [];

      const mergedDocs = baseDocs.map((doc: any) => {
        const uploadedFile = uploadedFiles.find(
          (file: any) => file.document_id === doc.document_id,
        );

        return uploadedFile ? { ...doc, uploadedFile } : doc;
      });

      const uploadedFileIds = new Set(
        uploadedFiles.map((file: any) => file.document_id),
      );

      const everyDocUploaded =
        baseDocs.length > 0 &&
        baseDocs.every((doc: any) => uploadedFileIds.has(doc.document_id));

      setAllDocuments(mergedDocs);
      setConsentCheck(everyDocUploaded);
    } catch (error) {
      console.error("Error fetching undertakings:", error);
      toast.error("Failed to load undertakings");
    }
  }, [dispatch, access_id, category_id]);

  const handleConsent = useCallback(async () => {
    if (!access_id) {
      toast.error("Application ID is missing");
      return;
    }

    try {
      const payload = createUndertakingPayload();
      const res = await dispatch(saveRowUndertakingThunk(payload));

      if (res?.meta?.requestStatus === "fulfilled") {
        await fetchAndMergeDocuments();
        setConsentCheck(true);
        toast.success("Undertakings and consent saved successfully");
      } else {
        throw new Error("Failed to save undertaking document");
      }
    } catch (error) {
      setConsentCheck(false);
      toast.error("Failed to save undertaking document");
    }
  }, [dispatch, createUndertakingPayload, fetchAndMergeDocuments, access_id]);

  useEffect(() => {
    fetchAndMergeDocuments();
  }, [fetchAndMergeDocuments]);

  useEffect(() => {
    if (setIsConsentValid) {
      setIsConsentValid(consentCheck);
    }
  }, [consentCheck, setIsConsentValid]);

  useEffect(() => {
    if (setSubmitHandler) {
      setSubmitHandler(null);
      return () => setSubmitHandler(null);
    }

    return undefined;
  }, [setSubmitHandler]);

  return (
    <Box className="amenities-container" component="div">
      {!isDetailsPage && (
        <h3 className="stepper-hd w-full body-l flex items-center justify-between">
          Undertakings and Consents
        </h3>
      )}

      <Box
        className={!isDetailsPage ? "form-wrapper" : "form-wrapper mt-4"}
        sx={{ paddingTop: 0 }}
      >
        {!isDetailsPage && (
          <div className="flex items-end justify-between w-full mb-4">
            <div>
              <h5 className="body-m gray-700 font-medium mt-3 mb-4">
                To continue, review and give consent for all undertakings below.
              </h5>
            </div>
          </div>
        )}

        <Card
          sx={{ boxShadow: "none", border: "1px solid #E7E8E9", p: 0, mb: 3 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            className="py-2 bg-gray-100 px-4 m-0 flex items-center justify-between cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <h4 className="body-m gray-900 font-medium">
              Applicant Declarations
            </h4>
            <IconButton
              aria-label="Collapse section"
              sx={{ bgcolor: "transparent", p: 0, height: 28, width: 28 }}
            >
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>

          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <CardContent>
              <UnderTackingListDetail
                profile={profile}
                amenity={amenity}
                utility_type={utility_type}
                details={details}
                proposed_utility_alignment={proposed_utility_alignment}
                legacyCheck={legacyContent}
              />

              {!isDetailsPage && (
                <Box className="px-4 py-1 mb-1" sx={{ bgcolor: "#F4F2F7" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={consentCheck}
                        checked={consentCheck}
                        onChange={handleConsent}
                        sx={{ "&.Mui-checked": { color: "#3A1C73" } }}
                      />
                    }
                    label="I/We hereby acknowledge and agree to abide by all the above terms, conditions, and undertakings."
                    sx={{
                      "& .MuiTypography-root": {
                        fontSize: "14px",
                        fontWeight: 600,
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Collapse>
        </Card>
      </Box>
    </Box>
  );
};

export default LegacyUnderTakingConsentRow;
